import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  configTaxaConvenienciaSchema,
  ConfigTaxaConvenienciaFormValues,
} from '../../../validation/configTaxaConvenienciaSchema';
import {
  ConfigTaxaConveniencia,
  ConfigTaxaConvenienciaInput,
} from '../../../services/configTaxaConvenienciaService';

export interface ConfigTaxaConvenienciaFormProps {
  initialData: ConfigTaxaConveniencia | null;
  onSubmit: (data: ConfigTaxaConvenienciaInput) => Promise<ConfigTaxaConveniencia>;
}

const toField = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

const ConfigTaxaConvenienciaForm: React.FC<ConfigTaxaConvenienciaFormProps> = ({
  initialData,
  onSubmit,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ConfigTaxaConvenienciaFormValues>({
    resolver: zodResolver(configTaxaConvenienciaSchema),
    defaultValues: {
      pixPercentual: toField(initialData?.pixPercentual),
      pixValorFixo: toField(initialData?.pixValorFixo),
      cartaoPercentual: toField(initialData?.cartaoPercentual),
      cartaoValorFixo: toField(initialData?.cartaoValorFixo),
    },
  });

  const submitHandler = async (values: ConfigTaxaConvenienciaFormValues) => {
    // O schema já transforma '' -> null e strings numéricas -> number
    const parsed = configTaxaConvenienciaSchema.parse(values);
    await onSubmit({
      pixPercentual: parsed.pixPercentual,
      pixValorFixo: parsed.pixValorFixo,
      cartaoPercentual: parsed.cartaoPercentual,
      cartaoValorFixo: parsed.cartaoValorFixo,
    });
  };

  const percentualAdornment = { endAdornment: <InputAdornment position="end">%</InputAdornment> };
  const reaisAdornment = { startAdornment: <InputAdornment position="start">R$</InputAdornment> };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A taxa de conveniência é cobrada uma única vez por compra, independentemente da
          quantidade de itens no carrinho. O percentual e o valor fixo são somados. Deixe em
          branco para não cobrar.
        </Typography>

        <form onSubmit={handleSubmit(submitHandler)}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            PIX
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="pixPercentual"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Percentual"
                  fullWidth
                  inputProps={{ step: '0.01', min: 0, max: 100 }}
                  InputProps={percentualAdornment}
                  error={!!errors.pixPercentual}
                  helperText={errors.pixPercentual?.message as string}
                />
              )}
            />
            <Controller
              name="pixValorFixo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Valor fixo"
                  fullWidth
                  inputProps={{ step: '0.01', min: 0 }}
                  InputProps={reaisAdornment}
                  error={!!errors.pixValorFixo}
                  helperText={errors.pixValorFixo?.message as string}
                />
              )}
            />
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Cartão de crédito
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="cartaoPercentual"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Percentual"
                  fullWidth
                  inputProps={{ step: '0.01', min: 0, max: 100 }}
                  InputProps={percentualAdornment}
                  error={!!errors.cartaoPercentual}
                  helperText={errors.cartaoPercentual?.message as string}
                />
              )}
            />
            <Controller
              name="cartaoValorFixo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Valor fixo"
                  fullWidth
                  inputProps={{ step: '0.01', min: 0 }}
                  InputProps={reaisAdornment}
                  error={!!errors.cartaoValorFixo}
                  helperText={errors.cartaoValorFixo?.message as string}
                />
              )}
            />
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

export default ConfigTaxaConvenienciaForm;
